
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import { ChatTemplate } from '@/entities/chat'
import { addTemplate, deleteTemplate, saveTemplate } from '@/api/chat'
import VueSlideUpDown from 'vue-slide-up-down'
import Dropdown from '@/components/Rain/Inputs/Dropdown.vue'
import { getLocations } from '@/api/appstore/locations'
import { getSavedTemplateLocations } from '@/api/chat'
import { notifyError } from '@/utils/appstore'
import Spinner from 'vue-simple-spinner'
import DropdownMultiCheckbox from '@/components/Rain/Inputs/DropdownMultiCheckbox.vue'
import { Getter } from 's-vuex-class'
import InfoTooltip from '@/components/InfoTooltip.vue'

declare const UIkit

@Component<ChatEditTemplate>({
    components: {
        InfoTooltip,
        DropdownMultiCheckbox,
        Dropdown,
        VueSlideUpDown,
        Spinner,
    },
})
export default class ChatEditTemplate extends Vue {
    @Prop(Number) public questionId!: number
    @Prop({ type: Object }) public template!: ChatTemplate
    @Prop(Array) public fields!: string[]
    @Prop(Number) public isFiveScore!: number
    @Prop(Function) public onTemplateUpdate // when action is done
    @Prop(Object) public customData!: { watching }
    @Getter public userPermissions

    public id: number = (this.template && Number(this.template.id)) || 0
    public name: string = (this.template && this.template.name) || ''
    public subject: string = (this.template && this.template.raw_subject) || ''
    public text: string = (this.template && this.template.raw_text) || ''
    public permanent = this.template ? Number(this.template.permanent || 0) : 0
    public visibility = this.template ? Number(this.template.private || 0) : 0
    public calendly_link: string =
        (this.template &&
            this.template.calendly &&
            this.template.calendly.link) ||
        ''
    public calendly_text: string =
        (this.template &&
            this.template.calendly &&
            this.template.calendly.text) ||
        ''
    public fieldCategory = ''

    private VISIBILITY_EVERYONE = 0
    private VISIBILITY_PRIVATE = 1
    private VISIBILITY_LOCATION = 2

    public loadingLocations = true
    public availableLocations: any[] = []
    public visibilityLocations: any[] = []

    public get visibilityOptions() {
        const opts: object[] = []
        if (this.id === 0 || this.template?.canDelete) {
            opts.push({
                label: 'Private',
                value: this.VISIBILITY_PRIVATE,
                tip: 'Only you can use, edit, or see this.',
            })
        }
        if (this.userPermissions.includes('ROLE_MANAGE_REPLY_TEMPLATES')) {
            opts.push({
                label: 'Everyone',
                value: this.VISIBILITY_EVERYONE,
                tip: 'Any user can see or use this template, and only those with Create & Edit Reply Templates permission can edit it',
            })
            opts.push({
                label: 'Location',
                value: this.VISIBILITY_LOCATION,
                tip: 'Only users associated with the chosen locations can see this template, and only those with Create & Edit Reply Templates permission can edit it ',
            })
        }
        return opts
    }

    public get userCanManageTemplates() {
        return this.userPermissions.includes('ROLE_MANAGE_REPLY_TEMPLATES')
    }

    public get locationOptions() {
        return this.availableLocations.map((location) => {
            return { label: location.location_name, value: location.id }
        })
    }

    public get selectedLocations() {
        return this.locationOptions.filter((location) => {
            return this.visibilityLocations.indexOf(location.value) >= 0
        })
    }

    public get htmlFields() {
        return (
            '<code class="field">&#123;' +
            this.fields.join('&#125;</code> <code class="field">&#123;') +
            '&#125;</code>'
        )
    }

    mounted() {
        if (Number(this.template?.private) === this.VISIBILITY_LOCATION) {
            this.loadLocations()
        }
        if (
            this.id === 0 &&
            !this.userPermissions.includes('ROLE_MANAGE_REPLY_TEMPLATES')
        ) {
            this.visibility = this.VISIBILITY_PRIVATE
        }
    }

    public async loadLocations() {
        this.loadingLocations = true
        const locationOptions = await getLocations()

        if (!locationOptions.success) {
            notifyError(
                'Something went wrong loading Locations. Reload the page and try again.'
            )
            return
        }

        this.availableLocations = locationOptions.data

        const savedLocations = await getSavedTemplateLocations(this.id)
        this.visibilityLocations = [
            ...this.visibilityLocations,
            ...savedLocations.data,
        ]

        this.loadingLocations = false
    }

    public onSetPrivate(value) {
        this.visibility = Number(value)
        if (this.visibility === this.VISIBILITY_LOCATION) {
            this.loadLocations()
        }
    }

    public onVisibilityLocationChange(opt, checked) {
        const indexVisibility = this.visibilityLocations.indexOf(opt.value)
        if (checked && indexVisibility < 0) {
            this.visibilityLocations.push(opt.value)
        } else if (
            !checked &&
            this.visibilityLocations.indexOf(opt.value) >= 0
        ) {
            this.visibilityLocations.splice(indexVisibility, 1)
        }
    }

    public onSelectFieldCategory(item) {
        if (item === this.fieldCategory) {
            this.fieldCategory = ''
        } else {
            this.fieldCategory = item
        }
    }

    public onCancel() {
        this.onTemplateUpdate(0)
    }

    public async onSave() {
        let ret
        // save template
        if (this.id) {
            ret = await saveTemplate(this.questionId, {
                name: this.name,
                subject: this.subject,
                text: this.text,
                id: this.id,
                calendly: {
                    link: this.calendly_link,
                    text: this.calendly_text,
                },
                private: this.visibility,
                visibilityLocations: this.visibilityLocations,
            })
        } else {
            ret = await addTemplate(this.questionId, {
                name: this.name,
                subject: this.subject,
                text: this.text,
                id: this.id,
                calendly: {
                    link: this.calendly_link,
                    text: this.calendly_text,
                },
                private: this.visibility,
                visibilityLocations: this.visibilityLocations,
            })
        }
        if (ret.data.success) {
            this.onTemplateUpdate(1)
        } else {
            UIkit.notify(ret.data.message, 'danger')
            return false
        }
    }

    public async onDelete() {
        if (confirm('Are you sure you want to delete this template?')) {
            // delete template first
            await deleteTemplate(this.questionId, this.id)
            this.onTemplateUpdate(1)
        }
    }
}
